import { AxiosResponse } from 'axios'
import { v4 } from 'uuid'

import { APP_CONFIG } from 'src/config.app'
import { BucketsAndCupsGroupDTO } from 'src/models/dto/activities/BucketsAndCupsGroupDTO'
import { ADAxios, ApiActionNames } from 'src/services/AxiosInterceptor'
import { handleAxiosError } from 'src/services/backend/ModuleService'

/**
 * Integration with the backend Buckets & Cups simulate API
 * (GenerateBucketsAndCupsSelectionPlan, POST bucketsAndCups/selectionPlan).
 *
 * It takes the B&C cups config and returns a selection plan: one execution per
 * administrable group, listing the buckets, cups, and item (page) version ids
 * that were selected.
 */

/** One group's selection result from the plan. */
export interface SelectionExecution {
    bucketIds: string[]
    cupIds: string[]
    itemVersionIds: string[]
}

export interface SelectionPlan {
    executions: SelectionExecution[]
}

interface PlanCup {
    cupId: string
    rank: number
    itemReferenceIds: string[]
}
interface PlanBucket {
    bucketId: string
    rank: number
    cups: PlanCup[]
}
interface PlanStep {
    bucketSelectionRule: string
    bucketRank?: number
    numberOfCupsToPickFromBucket: number
    cupSelectionRule: string
    cupRank?: number
    numberOfItemsToPickFromCup: number
    groupSize: number
}
interface SelectionPlanRequestPayload {
    moduleInstanceId: string
    buckets: PlanBucket[]
    steps: PlanStep[]
    idToMaxFrequencyMap: Record<string, number>
}

interface BuiltRequest {
    payload: SelectionPlanRequestPayload
    /** Maps a de-collided bucket id back to its original id (for the response). */
    bucketIdReverse: Map<string, string>
}

/**
 * Builds the request payload. Works around an id-namespace collision in some
 * modules where a bucket id equals a cup id (e.g. situation bucket "M2" has a
 * cup "M2_Main" while a separate bucket is also named "M2_Main"). The backend
 * tracks ranks/usages in a single id-keyed map, so colliding ids clobber each
 * other and bucket selection fails. We rename any bucket id that collides with
 * a cup id to a unique id before calling the API, then reverse-map it back in
 * the response so the caller still sees the original ids.
 */
function buildRequest(group: BucketsAndCupsGroupDTO): BuiltRequest {
    const cupIds = new Set<string>()
    const usedIds = new Set<string>()
    group.buckets.forEach((bucket) => {
        usedIds.add(bucket.bucketId)
        bucket.cups.forEach((cup) => {
            cupIds.add(cup.cupId)
            usedIds.add(cup.cupId)
        })
    })

    const bucketIdReverse = new Map<string, string>()
    const dedupeBucketId = (bucketId: string): string => {
        if (!cupIds.has(bucketId)) {
            return bucketId
        }
        let candidate = `${bucketId}__bucket`
        let suffix = 1
        while (usedIds.has(candidate)) {
            candidate = `${bucketId}__bucket${suffix}`
            suffix += 1
        }
        usedIds.add(candidate)
        bucketIdReverse.set(candidate, bucketId)
        return candidate
    }

    const idToMaxFrequencyMap: Record<string, number> = {}
    const buckets: PlanBucket[] = group.buckets.map((bucket) => {
        const bucketId = dedupeBucketId(bucket.bucketId)
        idToMaxFrequencyMap[bucketId] = bucket.maxFrequency
        return {
            bucketId,
            rank: bucket.rank,
            cups: bucket.cups.map((cup) => {
                idToMaxFrequencyMap[cup.cupId] = cup.maxFrequency
                return {
                    cupId: cup.cupId,
                    rank: cup.rank,
                    itemReferenceIds: cup.itemIds,
                }
            }),
        }
    })

    const steps: PlanStep[] = group.bucketAndCupSelectionStep.map((step) => ({
        bucketSelectionRule: step.bucketSelectionRule,
        bucketRank: step.bucketRank,
        numberOfCupsToPickFromBucket: step.numberOfCupsToPickFromBucket ?? 1,
        cupSelectionRule: step.cupSelectionRule,
        cupRank: step.cupRank,
        numberOfItemsToPickFromCup: step.numberOfItemsToPickFromCup ?? 1,
        groupSize: step.groupSize,
    }))

    return {
        payload: {
            // The simulate API treats moduleInstanceId as an ephemeral, per-call
            // identifier used only to scope usage tracking within a single
            // simulation, so a fresh uuid per request is intentional.
            moduleInstanceId: v4(),
            buckets,
            steps,
            idToMaxFrequencyMap,
        },
        bucketIdReverse,
    }
}

export class BucketsAndCupsSelectionPlanService {
    /** Simulate a delivery: returns the selection plan for the given B&C group. */
    public static async generateSelectionPlan(
        group: BucketsAndCupsGroupDTO
    ): Promise<SelectionPlan> {
        try {
            const { payload, bucketIdReverse } = buildRequest(group)
            const { data }: AxiosResponse<SelectionPlan> = await ADAxios.post(
                `${APP_CONFIG.backendAPIBaseUrl}/bucketsAndCups/selectionPlan`,
                payload,
                {
                    apiActionName: ApiActionNames.GenerateBucketsAndCupsSelectionPlan,
                    headers: { 'Content-Type': 'application/json' },
                }
            )
            // Reverse the de-collision so callers see the original bucket ids.
            return {
                executions: data.executions.map((execution) => ({
                    ...execution,
                    bucketIds: execution.bucketIds.map((id) => bucketIdReverse.get(id) ?? id),
                })),
            }
        } catch (e) {
            handleAxiosError(e)
        }
    }
}
